import { renderLiquid } from "./liquid"

export const WebhookBodyFormat = {
  json: "json",
  formUrlEncoded: "formUrlEncoded",
  text: "text",
} as const

export type WebhookBodyFormat = (typeof WebhookBodyFormat)[keyof typeof WebhookBodyFormat]

export const WEBHOOK_BODY_CONTENT_TYPE: Record<WebhookBodyFormat, string> = {
  json: "application/json",
  formUrlEncoded: "application/x-www-form-urlencoded",
  text: "text/plain",
}

function fixJsonTemplatesForParse(template: string) {
  return template.replace(/:\s*(\{\{[^}]+\}\})(?=\s*[,}\]])/g, (_, tmpl) => `: "${tmpl.trim()}"`)
}

export function isWebhookBodyTemplateInvalid(
  body: string | undefined | null,
  format: WebhookBodyFormat,
  method: string,
) {
  if (method === "GET") return false

  const trimmed = (body ?? "").trim()
  if (trimmed === "") return true

  if (format === "text") return false

  if (format === "formUrlEncoded") {
    try {
      const parsed: unknown = JSON.parse(fixJsonTemplatesForParse(trimmed))
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return false
    } catch {
      // fall through to raw key=value validation
    }
    return !trimmed.includes("=")
  }

  try {
    JSON.parse(fixJsonTemplatesForParse(trimmed))
    return false
  } catch {
    return true
  }
}

export function variablesToNestedContext(variables: Record<string, string>) {
  const result: Record<string, unknown> = {}

  for (const [path, value] of Object.entries(variables)) {
    const segments = path.split(".")
    let current: Record<string, unknown> = result

    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i]!
      if (
        !(segment in current) ||
        typeof current[segment] !== "object" ||
        current[segment] === null
      ) {
        current[segment] = {}
      }
      current = current[segment] as Record<string, unknown>
    }

    current[segments[segments.length - 1]!] = value
  }

  return result
}

const CHAIN_STEP_VARIABLE_PREFIX = /^steps\[\d+]/

export function parseWebhookPriorResponseBody(body: string) {
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    // fall through
  }
  return { _rawText: body }
}

export function buildWebhookTestContext(
  variableValues: Record<string, string>,
  priorResponses?: string[],
) {
  const nonStepVariables = Object.fromEntries(
    Object.entries(variableValues).filter(([key]) => !CHAIN_STEP_VARIABLE_PREFIX.test(key)),
  )
  const context: Record<string, unknown> = variablesToNestedContext(nonStepVariables)

  if (!priorResponses?.length) return context

  const steps = priorResponses.map(parseWebhookPriorResponseBody)

  for (const [path, value] of Object.entries(variableValues)) {
    const match = /^steps\[(\d+)]\.(.+)$/.exec(path)
    if (!match || !value.trim()) continue

    const stepIndex = Number(match[1])
    const key = match[2]!
    if (!steps[stepIndex]) steps[stepIndex] = {}
    steps[stepIndex]![key] = value
  }

  return { ...context, steps }
}

export function buildWebhookHeaders(
  webhookHeaders: { name: string; value: string }[],
  contentType: string,
  context: Record<string, unknown>,
) {
  return assembleWebhookHeaders(
    webhookHeaders.map((header) => ({ ...header, value: renderLiquid(header.value, context) })),
    contentType,
  )
}

// Expects header values that are already rendered
export function assembleWebhookHeaders(
  renderedHeaders: { name: string; value: string }[],
  contentType: string,
) {
  const headers: Record<string, string> = {}

  for (const header of renderedHeaders) {
    if (header.name.toLowerCase() === "content-type") continue
    headers[header.name] = header.value
  }

  headers["Content-Type"] = contentType
  return headers
}

export function renderWebhookBody(
  template: string,
  format: WebhookBodyFormat,
  context: Record<string, unknown>,
) {
  const trimmed = template.trim()

  if (isEmptyWebhookBody(trimmed)) {
    return { bodyString: undefined, contentType: WEBHOOK_BODY_CONTENT_TYPE[format] }
  }

  return formatWebhookBody(
    renderLiquid(trimmed, context, { jsonSafe: isWebhookBodyJsonSafe(format) }),
    format,
  )
}

export function isEmptyWebhookBody(template: string) {
  const trimmed = template.trim()
  return !trimmed || trimmed === "{}"
}

export function isWebhookBodyJsonSafe(format: WebhookBodyFormat) {
  return format !== "text"
}

// Expects a body template that is already rendered
export function formatWebhookBody(rendered: string, format: WebhookBodyFormat) {
  const contentType = WEBHOOK_BODY_CONTENT_TYPE[format]

  if (format === "text") {
    return { bodyString: rendered, contentType }
  }

  if (format === "formUrlEncoded") {
    try {
      const parsed: unknown = JSON.parse(rendered)
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const params = new URLSearchParams()
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
          if (value === null || value === undefined) continue
          params.set(key, String(value))
        }
        const bodyString = params.toString()
        return { bodyString: bodyString || undefined, contentType }
      }
    } catch {
      // fall through to raw key=value body
    }

    return { bodyString: rendered, contentType }
  }

  try {
    const parsed: unknown = JSON.parse(rendered)
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length > 0
    ) {
      return { bodyString: JSON.stringify(parsed), contentType }
    }
  } catch (error) {
    throw new Error(
      `Invalid webhook body JSON: ${error instanceof Error ? error.message : String(error)}`,
    )
  }

  return { bodyString: undefined, contentType }
}

export function previewWebhookBody(template: string, format: WebhookBodyFormat) {
  const trimmed = template.trim()
  if (!trimmed || trimmed === "{}") return format === "text" ? "" : "{}"

  if (format === "text") return trimmed

  const fixedJson = fixJsonTemplatesForParse(trimmed)

  if (format === "formUrlEncoded") {
    try {
      const parsed: unknown = JSON.parse(fixedJson)
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const params = new URLSearchParams()
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof value === "string" && value.includes("{{") && value.includes("}}")) {
            params.set(key, "0")
            continue
          }
          if (value === null || value === undefined) continue
          params.set(key, String(value))
        }
        return params.toString()
      }
    } catch {
      return trimmed
    }
  }

  try {
    const parsed: unknown = JSON.parse(fixedJson)
    const process = (value: unknown): unknown => {
      if (typeof value === "string" && value.includes("{{") && value.includes("}}")) {
        return 0
      }
      if (Array.isArray(value)) return value.map(process)
      if (value !== null && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, process(v)]))
      }
      return value
    }
    return JSON.stringify(process(parsed), null, 2)
  } catch {
    return null
  }
}
